// components/Configuration/BillingSection.jsx
import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Calendar, Lock, Globe, Receipt, X, Check, 
  RefreshCw, AlertCircle, Download, ExternalLink, CheckCircle,
  XCircle, Clock, FileText 
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useI18n } from '../../hooks/useI18n';
import { useAuthStore } from '../../stores/authStore';

// Utilidades de validación
const validateCardNumber = (number) => {
  const cleaned = number.replace(/\s+/g, '');
  
  // Verificar longitud
  if (!/^[0-9]{13,19}$/.test(cleaned)) {
    return false;
  }
  
  // Algoritmo de Luhn
  let sum = 0;
  let isEven = false;
  
  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned.charAt(i), 10);
    
    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    
    sum += digit;
    isEven = !isEven;
  }
  
  return (sum % 10) === 0;
};

const validateExpiryDate = (date) => {
  const regex = /^(0[1-9]|1[0-2])\/([0-9]{2})$/;
  if (!regex.test(date)) return false;
  
  const [month, year] = date.split('/');
  const currentDate = new Date();
  const expiryDate = new Date(2000 + parseInt(year), parseInt(month) - 1);
  
  return expiryDate > currentDate;
};

const validateCVC = (cvc) => {
  const cvcRegex = /^[0-9]{3,4}$/;
  return cvcRegex.test(cvc);
};

const formatCardNumber = (value) => {
  const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
  const matches = v.match(/\d{4,16}/g);
  const match = matches && matches[0] || '';
  const parts = [];
  for (let i = 0, len = match.length; i < len; i += 4) {
    parts.push(match.substring(i, i + 4));
  }
  if (parts.length) {
    return parts.join(' ');
  } else {
    return v;
  }
};

const formatExpiryDate = (value) => {
  const v = value.replace(/\D/g, '');
  if (v.length >= 2) {
    return v.substring(0, 2) + '/' + v.substring(2, 4);
  }
  return v;
};

const getCardType = (number) => {
  const patterns = {
    visa: /^4/,
    mastercard: /^5[1-5]/,
    amex: /^3[47]/,
  };
  
  for (const [type, pattern] of Object.entries(patterns)) {
    if (pattern.test(number.replace(/\s+/g, ''))) {
      return type;
    }
  }
  return null;
};

// Modal de Método de Pago
const PaymentMethodModal = ({ isOpen, onClose, onSave, currentPlan }) => {
  const { isDark } = useTheme();
  const { t } = useI18n();
  
  const [formData, setFormData] = useState({
    fullName: '',
    country: 'Chile',
    addressLine1: '',
    cardNumber: '',
    expiryDate: '',
    cvc: '',
    taxId: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field, value) => {
    let formattedValue = value;
    
    if (field === 'cardNumber') {
      formattedValue = formatCardNumber(value);
    } else if (field === 'expiryDate') {
      formattedValue = formatExpiryDate(value);
    } else if (field === 'cvc') {
      formattedValue = value.replace(/\D/g, '').substring(0, 4);
    }
    
    setFormData(prev => ({ ...prev, [field]: formattedValue }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'El nombre completo es requerido';
    }
    
    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'La dirección es requerida';
    }
    
    if (!validateCardNumber(formData.cardNumber)) {
      newErrors.cardNumber = 'Número de tarjeta inválido';
    }
    
    if (!validateExpiryDate(formData.expiryDate)) {
      newErrors.expiryDate = 'Fecha de expiración inválida';
    }
    
    if (!validateCVC(formData.cvc)) {
      newErrors.cvc = 'CVC inválido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      onSave(formData);
      onClose();
    } catch (error) {
      setErrors({ general: 'Error al validar la tarjeta' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const cardType = getCardType(formData.cardNumber);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="flex justify-between items-center mb-6">
          <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Agregar Método de Pago
          </h3>
          <button 
            onClick={onClose} 
            className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <X size={20} />
          </button>
        </div>

        {errors.general && (
          <div className={`mb-4 p-3 rounded ${isDark ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'}`}>
            {errors.general}
          </div>
        )}

        <div className="space-y-4">
          {/* Plan Info */}
          <div className={`p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Plan seleccionado
            </div>
            <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {currentPlan.name}
            </div>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {currentPlan.billing}
            </div>
          </div>

          {/* Nombre Completo */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Nombre Completo
            </label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 ${
                errors.fullName 
                  ? 'border-red-500 focus:ring-red-500' 
                  : isDark
                    ? 'bg-gray-700 border-gray-600 focus:ring-blue-500 text-white'
                    : 'bg-white border-gray-300 focus:ring-blue-500 text-gray-900'
              }`}
              placeholder="Juan Pérez"
            />
            {errors.fullName && (
              <p className={`text-sm mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                {errors.fullName}
              </p>
            )}
          </div>

          {/* País */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              País
            </label>
            <div className="relative">
              <Globe className={`absolute left-3 top-2.5 h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              <select
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                className={`w-full pl-10 pr-3 py-2 border rounded focus:outline-none focus:ring-2 ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 focus:ring-blue-500 text-white'
                    : 'bg-white border-gray-300 focus:ring-blue-500 text-gray-900'
                }`}
              >
                <option value="Chile">Chile</option>
                <option value="Argentina">Argentina</option>
                <option value="Peru">Perú</option>
                <option value="Colombia">Colombia</option>
                <option value="Mexico">México</option>
              </select>
            </div>
          </div>

          {/* Dirección */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Dirección
            </label>
            <input
              type="text"
              value={formData.addressLine1}
              onChange={(e) => handleInputChange('addressLine1', e.target.value)}
              className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 ${
                errors.addressLine1 
                  ? 'border-red-500 focus:ring-red-500' 
                  : isDark
                    ? 'bg-gray-700 border-gray-600 focus:ring-blue-500 text-white'
                    : 'bg-white border-gray-300 focus:ring-blue-500 text-gray-900'
              }`}
              placeholder="Calle Principal 123"
            />
            {errors.addressLine1 && (
              <p className={`text-sm mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                {errors.addressLine1}
              </p>
            )}
          </div>

          {/* Número de Tarjeta */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Número de Tarjeta
            </label>
            <div className="relative">
              <CreditCard className={`absolute left-3 top-2.5 h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              <input
                type="text"
                value={formData.cardNumber}
                onChange={(e) => handleInputChange('cardNumber', e.target.value)}
                className={`w-full pl-10 pr-20 py-2 border rounded focus:outline-none focus:ring-2 ${
                  errors.cardNumber 
                    ? 'border-red-500 focus:ring-red-500' 
                    : isDark
                      ? 'bg-gray-700 border-gray-600 focus:ring-blue-500 text-white'
                      : 'bg-white border-gray-300 focus:ring-blue-500 text-gray-900'
                }`}
                placeholder="1234 1234 1234 1234"
                maxLength="19"
              />
              <div className="absolute right-3 top-2.5 flex space-x-1">
                <div className={`w-6 h-4 rounded text-xs flex items-center justify-center ${
                  cardType === 'visa' ? 'bg-blue-600 text-white' : 'bg-gray-600'
                }`}>V</div>
                <div className={`w-6 h-4 rounded text-xs flex items-center justify-center ${
                  cardType === 'mastercard' ? 'bg-red-600 text-white' : 'bg-gray-600'
                }`}>M</div>
                <div className={`w-6 h-4 rounded text-xs flex items-center justify-center ${
                  cardType === 'amex' ? 'bg-green-600 text-white' : 'bg-gray-600'
                }`}>A</div>
              </div>
            </div>
            {errors.cardNumber && (
              <p className={`text-sm mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                {errors.cardNumber}
              </p>
            )}
          </div>

          {/* Fecha de Expiración y CVC */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Fecha de Expiración
              </label>
              <div className="relative">
                <Calendar className={`absolute left-3 top-2.5 h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                <input
                  type="text"
                  value={formData.expiryDate}
                  onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                  className={`w-full pl-10 pr-3 py-2 border rounded focus:outline-none focus:ring-2 ${
                    errors.expiryDate 
                      ? 'border-red-500 focus:ring-red-500' 
                      : isDark
                        ? 'bg-gray-700 border-gray-600 focus:ring-blue-500 text-white'
                        : 'bg-white border-gray-300 focus:ring-blue-500 text-gray-900'
                  }`}
                  placeholder="MM/AA"
                  maxLength="5"
                />
              </div>
              {errors.expiryDate && (
                <p className={`text-sm mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                  {errors.expiryDate}
                </p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                CVC
              </label>
              <div className="relative">
                <Lock className={`absolute left-3 top-2.5 h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                <input
                  type="text"
                  value={formData.cvc}
                  onChange={(e) => handleInputChange('cvc', e.target.value)}
                  className={`w-full pl-10 pr-3 py-2 border rounded focus:outline-none focus:ring-2 ${
                    errors.cvc 
                      ? 'border-red-500 focus:ring-red-500' 
                      : isDark
                        ? 'bg-gray-700 border-gray-600 focus:ring-blue-500 text-white'
                        : 'bg-white border-gray-300 focus:ring-blue-500 text-gray-900'
                  }`}
                  placeholder="123"
                  maxLength="4"
                />
              </div>
              {errors.cvc && (
                <p className={`text-sm mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                  {errors.cvc}
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
            >
              {isLoading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal de Cambio de Plan
const ChangePlanModal = ({ isOpen, onClose, currentPlan, onChangePlan }) => {
  const { isDark } = useTheme();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  const plans = [
    {
      id: 'standard',
      name: 'Standard',
      price: '$70',
      period: '/mes',
      features: ['50 consultas/día', 'Soporte email', 'Documentación básica']
    },
    {
      id: 'profesional',
      name: 'Profesional',
      price: '$100',
      period: '/mes',
      features: ['Consultas ilimitadas', 'Soporte prioritario', 'Documentación especializada'],
      popular: true
    },
    {
      id: 'premium',
      name: 'Premium',
      price: '$150',
      period: '/mes',
      features: ['Consultas ilimitadas', 'Soporte WhatsApp', 'Documentación especializada', 'Soporte prioritario']
    }
  ];

  const handleChangePlan = async () => {
    if (!selectedPlan) return;
    
    setLoading(true);
    try {
      await onChangePlan(selectedPlan);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6 border-b border-gray-700">
          <div className="flex justify-between items-center">
            <div>
              <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Cambiar Plan de Suscripción
              </h3>
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Plan actual: <span className="font-semibold">{currentPlan.name}</span>
              </p>
            </div>
            <button 
              onClick={onClose} 
              className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Alerta importante */}
          <div className={`mb-6 p-4 rounded-lg ${isDark ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'} border`}>
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-yellow-400' : 'text-yellow-800'}`}>
                  ⚠️ Importante: Cambio de Plan
                </p>
                <p className={`text-sm mt-1 ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                  Al cambiar tu plan, deberás cerrar sesión y volver a iniciar sesión para que los cambios surtan efecto.
                  Serás redirigido automáticamente al login después del cambio.
                </p>
              </div>
            </div>
          </div>

          {/* Grid de planes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const isCurrentPlan = currentPlan.name === plan.name;
              const isSelected = selectedPlan === plan.id;
              
              return (
                <div
                  key={plan.id}
                  onClick={() => !isCurrentPlan && setSelectedPlan(plan.id)}
                  className={`relative p-6 rounded-lg border-2 transition-all cursor-pointer ${
                    isCurrentPlan
                      ? isDark
                        ? 'border-gray-600 bg-gray-700/50 cursor-not-allowed opacity-60'
                        : 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-60'
                      : isSelected
                      ? 'border-blue-500 bg-blue-500/10'
                      : isDark
                      ? 'border-gray-700 hover:border-gray-600'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        MÁS POPULAR
                      </span>
                    </div>
                  )}
                  
                  {isCurrentPlan && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className={`${isDark ? 'bg-green-600' : 'bg-green-500'} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                        PLAN ACTUAL
                      </span>
                    </div>
                  )}

                  <h4 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {plan.name}
                  </h4>
                  
                  <div className="mb-4">
                    <span className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {plan.price}
                    </span>
                    <span className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {plan.period}
                    </span>
                  </div>

                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className={`flex items-start ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {isSelected && !isCurrentPlan && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle className="w-6 h-6 text-blue-500" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className={`px-6 py-2 rounded ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleChangePlan}
              disabled={!selectedPlan || loading}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Cambiando plan...' : 'Confirmar Cambio de Plan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BillingSection = () => {
  const { isDark } = useTheme();
  const { t } = useI18n();
  const { user, clearAuth } = useAuthStore();
  
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState(null);
  
  const [currentPlan, setCurrentPlan] = useState({
    name: 'Standard',
    type: 'Plan básico',
    billing: '$50/mes'
  });

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token') || localStorage.getItem('kikai_token');
      
      if (!token) {
        setError('No hay sesión activa');
        return;
      }

      const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

      // Cargar transacciones
      const transactionsRes = await fetch(`${API_BASE}/user/transactions?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (transactionsRes.ok) {
        const transactionsData = await transactionsRes.json();
        if (transactionsData.success) {
          setTransactions(transactionsData.transactions || []);
          console.log('✅ Transacciones cargadas:', transactionsData.transactions?.length);
        }
      }

      // Cargar métodos de pago
      const paymentMethodsRes = await fetch(`${API_BASE}/payment-methods`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (paymentMethodsRes.ok) {
        const paymentMethodsData = await paymentMethodsRes.json();
        const methods = paymentMethodsData.payment_methods || [];
        if (methods.length > 0) {
          const defaultMethod = methods.find(m => m.is_default) || methods[0];
          setPaymentMethod({
            last4: defaultMethod.card_last_four,
            cardType: defaultMethod.card_brand || 'visa',
            country: defaultMethod.country || 'Chile'
          });
        }
      }

      // Actualizar plan actual
      if (user?.subscription_type) {
        const planNames = {
          'premium': 'Premium',
          'profesional': 'Profesional',
          'professional': 'Profesional',
          'standard': 'Standard'
        };
        
        const planPrices = {
          'premium': '$179/mes',
          'profesional': '$89/mes',
          'professional': '$89/mes',
          'standard': '$50/mes'
        };
        
        setCurrentPlan({
          name: planNames[user.subscription_type] || 'Standard',
          type: `Plan ${planNames[user.subscription_type] || 'Standard'}`,
          billing: planPrices[user.subscription_type] || '$50/mes'
        });
      }

    } catch (err) {
      console.error('❌ Error cargando datos de facturación:', err);
      setError('Error al cargar la información de facturación');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePaymentMethod = (newPaymentData) => {
    setPaymentMethod({
      ...newPaymentData,
      last4: newPaymentData.cardNumber.slice(-4),
      cardType: getCardType(newPaymentData.cardNumber)
    });
  };

  const handleChangePlan = async (newPlanId) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('kikai_token');
      const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

      // Llamar al endpoint de cambio de plan
      const response = await fetch(`${API_BASE}/api/user/change-plan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          newPlan: newPlanId
        })
      });

      const data = await response.json();

      if (data.success) {
        // Mostrar mensaje de éxito
        alert('✅ Plan cambiado exitosamente. Serás redirigido al login para actualizar tu sesión.');
        
        // Cerrar sesión
        clearAuth();
        
        // Redirigir al login
        window.location.href = '/login';
      } else {
        throw new Error(data.error || 'Error al cambiar el plan');
      }
    } catch (error) {
      console.error('❌ Error cambiando plan:', error);
      alert(`Error al cambiar el plan: ${error.message}`);
    }
  };

  const handleCancelPlan = () => {
    if (confirm('¿Estás seguro de que deseas cancelar tu suscripción?')) {
      console.log('Plan cancelado');
      // TODO: Implementar cancelación
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: currency?.toUpperCase() || 'USD'
    }).format(amount);
  };

  const getStatusConfig = (status) => {
    const configs = {
      completed: {
        label: 'Pagado',
        icon: CheckCircle,
        color: 'text-green-600',
        bg: isDark ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
      },
      pending: {
        label: 'Pendiente',
        icon: Clock,
        color: 'text-yellow-600',
        bg: isDark ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800'
      },
      failed: {
        label: 'Fallido',
        icon: XCircle,
        color: 'text-red-600',
        bg: isDark ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'
      }
    };
    
    return configs[status] || configs.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin mr-3" />
        <span className={isDark ? 'text-white' : 'text-gray-900'}>
          Cargando información de facturación...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 rounded-lg border ${isDark ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
          <span className={isDark ? 'text-red-400' : 'text-red-800'}>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Encabezado de Plan */}
      <div className={`rounded-lg p-6 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className="bg-blue-600 p-2 rounded">
              <CreditCard className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {currentPlan.name}
              </h3>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                {currentPlan.type}
              </p>
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {currentPlan.billing}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowChangePlanModal(true)}
            className={`px-4 py-2 rounded transition-colors text-sm ${
              isDark 
                ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
            }`}
          >
            Cambiar Plan
          </button>
        </div>
      </div>

      {/* Método de Pago */}
      <div className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className={`p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <h3 className={`text-lg font-semibold flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <span className="mr-2">💳</span>
              {t('billing.payment.title')}
            </h3>
            <button
              onClick={() => setShowPaymentModal(true)}
              className="text-blue-400 hover:text-blue-300 text-sm"
            >
              {t('billing.payment.update')}
            </button>
          </div>
        </div>
        <div className="p-6">
          {paymentMethod ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CreditCard className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                <div>
                  <p className={isDark ? 'text-white' : 'text-gray-900'}>
                    •••• •••• •••• {paymentMethod.last4}
                  </p>
                  <p className={`text-sm capitalize ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {paymentMethod.cardType} • {paymentMethod.country}
                  </p>
                </div>
              </div>
              <Check className="h-5 w-5 text-green-400" />
            </div>
          ) : (
            <div className="text-center py-4">
              <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Los pagos se procesan mediante PayPal
              </p>
              <a
                href="https://www.paypal.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-500 hover:text-blue-600"
              >
                Más información sobre PayPal
                <ExternalLink className="w-4 h-4 ml-1" />
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Historial de Transacciones */}
      <div className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className={`p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h3 className={`text-lg font-semibold flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Receipt className="h-5 w-5 mr-2" />
            {t('billing.invoices.title')}
          </h3>
        </div>
        
        {transactions.length === 0 ? (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No hay transacciones registradas</p>
            <p className="text-sm mt-1">Tus pagos aparecerán aquí</p>
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="w-full">
              <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  <th className={`text-left py-3 px-6 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('billing.invoices.date')}
                  </th>
                  <th className={`text-left py-3 px-6 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Descripción
                  </th>
                  <th className={`text-left py-3 px-6 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('billing.invoices.total')}
                  </th>
                  <th className={`text-left py-3 px-6 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('billing.invoices.status')}
                  </th>
                  <th className={`text-right py-3 px-6 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('billing.invoices.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {transactions.map((transaction) => {
                  const statusConfig = getStatusConfig(transaction.status);
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <tr key={transaction.id} className={isDark ? 'hover:bg-gray-750' : 'hover:bg-gray-50'}>
                      <td className={`py-4 px-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {formatDate(transaction.created_at)}
                      </td>
                      <td className={`py-4 px-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {transaction.description}
                      </td>
                      <td className={`py-4 px-6 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatCurrency(transaction.amount, transaction.currency)}
                      </td>
                      <td className="py-4 px-6">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right">
                        {transaction.status === 'completed' && (
                          <button className="text-blue-400 hover:text-blue-300 text-sm inline-flex items-center">
                            <Download className="w-4 h-4 mr-1" />
                            {t('billing.invoices.view')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Información de PayPal */}
      <div className={`p-4 rounded-lg border ${isDark ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <p className={`text-sm font-medium ${isDark ? 'text-blue-400' : 'text-blue-800'}`}>
              Procesamiento de Pagos Seguro
            </p>
            <p className={`text-sm mt-1 ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
              Todos los pagos son procesados de forma segura a través de PayPal. 
              No almacenamos información sensible de tarjetas de crédito en nuestros servidores.
            </p>
          </div>
        </div>
      </div>

      {/* Cancelación */}
      <div className={`rounded-lg p-6 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('billing.cancel.title')}
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <p className={isDark ? 'text-gray-300' : 'text-gray-700'}>
              {t('billing.cancel.description')}
            </p>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('billing.cancel.note')}
            </p>
          </div>
          <button
            onClick={handleCancelPlan}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
          >
            {t('billing.cancel.button')}
          </button>
        </div>
      </div>

      {/* Modal de Método de Pago */}
      <PaymentMethodModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSave={handleUpdatePaymentMethod}
        currentPlan={currentPlan}
      />

      {/* Modal de Cambio de Plan */}
      <ChangePlanModal
        isOpen={showChangePlanModal}
        onClose={() => setShowChangePlanModal(false)}
        currentPlan={currentPlan}
        onChangePlan={handleChangePlan}
      />
    </div>
  );
};

export default BillingSection;